// A minimal ZIP writer, so a batch conversion comes back as one file.
//
// Browsers block a page that starts more than a couple of downloads in a row,
// which makes handing over twenty converted images one at a time unworkable.
// A library would do this too, but the archive needed here is the simplest
// shape there is: entries are stored, never deflated, because JPEG, WebP and
// PNG are already compressed and running deflate over them buys nothing.
//
// Format: https://pkware.cachefly.net/webdocs/APPNOTE/APPNOTE-6.3.9.TXT

const LOCAL_HEADER = 0x04034b50;
const CENTRAL_HEADER = 0x02014b50;
const END_OF_DIRECTORY = 0x06054b50;
const STORED = 0;

const crcTable = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
        let c = n;
        for (let k = 0; k < 8; k += 1) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[n] = c >>> 0;
    }
    return table;
})();

function crc32(bytes) {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
}

/** The time fields, in the two packed 16-bit words MS-DOS used. */
function dosTime(date) {
    return {
        // Seconds have one bit less than they need, so they count in twos.
        time: (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1),
        // Year is an offset from 1980, which is where the format starts.
        date: ((Math.max(1980, date.getFullYear()) - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
    };
}

/**
 * Pack files into a ZIP.
 *
 * @param {Array<{name: string, blob: Blob}>} files Names are used as given, so
 *        the caller is responsible for making them unique.
 * @returns {Promise<Blob>}
 */
export async function zip(files) {
    const encoder = new TextEncoder();
    const stamp = dosTime(new Date());

    const parts = [];
    const directory = [];
    let offset = 0;

    for (const file of files) {
        const name = encoder.encode(file.name);
        const data = new Uint8Array(await file.blob.arrayBuffer());
        const checksum = crc32(data);

        const local = new DataView(new ArrayBuffer(30));
        local.setUint32(0, LOCAL_HEADER, true);
        local.setUint16(4, 20, true);            // Version needed: 2.0
        local.setUint16(6, 0x0800, true);        // Flags: the name is UTF-8
        local.setUint16(8, STORED, true);
        local.setUint16(10, stamp.time, true);
        local.setUint16(12, stamp.date, true);
        local.setUint32(14, checksum, true);
        local.setUint32(18, data.length, true);  // Compressed size
        local.setUint32(22, data.length, true);  // Uncompressed size
        local.setUint16(26, name.length, true);
        local.setUint16(28, 0, true);            // No extra field

        parts.push(new Uint8Array(local.buffer), name, data);

        const central = new DataView(new ArrayBuffer(46));
        central.setUint32(0, CENTRAL_HEADER, true);
        central.setUint16(4, 20, true);          // Version made by
        central.setUint16(6, 20, true);          // Version needed
        central.setUint16(8, 0x0800, true);
        central.setUint16(10, STORED, true);
        central.setUint16(12, stamp.time, true);
        central.setUint16(14, stamp.date, true);
        central.setUint32(16, checksum, true);
        central.setUint32(20, data.length, true);
        central.setUint32(24, data.length, true);
        central.setUint16(28, name.length, true);
        central.setUint16(30, 0, true);          // Extra field length
        central.setUint16(32, 0, true);          // Comment length
        central.setUint16(34, 0, true);          // Disk number
        central.setUint16(36, 0, true);          // Internal attributes
        central.setUint32(38, 0, true);          // External attributes
        central.setUint32(42, offset, true);     // Where the local header sits

        directory.push(new Uint8Array(central.buffer), name);
        offset += 30 + name.length + data.length;
    }

    const directorySize = directory.reduce((sum, part) => sum + part.length, 0);

    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, END_OF_DIRECTORY, true);
    end.setUint16(4, 0, true);                   // This disk
    end.setUint16(6, 0, true);                   // Disk the directory starts on
    end.setUint16(8, files.length, true);
    end.setUint16(10, files.length, true);
    end.setUint32(12, directorySize, true);
    end.setUint32(16, offset, true);
    end.setUint16(20, 0, true);                  // Comment length

    return new Blob([...parts, ...directory, new Uint8Array(end.buffer)], { type: 'application/zip' });
}
